/**
 * NumberingForm — Settings → Numbering. Single FormCard wraps a
 * tabular grid; one row per V1.0 doc type.
 *
 * Row layout (icon + 4 controls + action):
 *
 *   ┌───┬──────────────┬──────────┬──────────┬───────────────────┬──────────────┐
 *   │ ◎ │ Tax invoice  │ [INV  ]  │ [ 4 v]   │ INV/2026-27/0143  │ Continue ⇨  │
 *   │   │ 142 issued   │          │          │                   │              │
 *   ├───┼──────────────┼──────────┼──────────┼───────────────────┼──────────────┤
 *   │ ◎ │ Credit note  │ [CN   ]  │ [ 4 v]   │ CN/2026-27/0009   │ Locked       │
 *   │   │ 8 issued     │          │          │                   │              │
 *   └───┴──────────────┴──────────┴──────────┴───────────────────┴──────────────┘
 *
 * Visual conventions:
 *   - Doc-type icon chip on the left (sky tint when active, muted when
 *     the row has nothing issued yet).
 *   - 2-px sky left-accent appears when the row is dirty vs baseline.
 *   - Preview is rendered as a code chip (sky-50 bg, mono, ring) so the
 *     final string visually stands out as the source-of-truth output.
 *   - Issued-count is a StatusPill — `ok` tone above 0, `muted` when 0.
 *
 * Adding a new doc type in V1.x is one entry in
 * `V1_NUMBERING_DOCUMENT_TYPES` + `DOCUMENT_TYPE_LABEL` — the form
 * grows by one row, no other UI changes.
 *
 * @see prototype/settings/settings-numbering.html — visual reference (table-style)
 */
import * as React from "react";
import {
  FileText,
  Hash,
  Receipt,
  ArrowDownToLine,
  Pencil,
  Lock,
  type LucideIcon,
} from "lucide-react";
import {
  Alert,
  Input,
  Button,
  StatusPill,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  cn,
} from "@flint/ui";
import { FormCard } from "../form-card.tsx";
import {
  formatPreview,
  isCardDirty,
  type NumberingFormState,
  type NumberingCardValues,
} from "./lib/form-defaults.ts";

const DOC_TYPE_ICON: Record<string, LucideIcon> = {
  "tax-invoice": FileText,
  "credit-note": ArrowDownToLine,
  receipt: Receipt,
  "vendor-payment": Hash,
};

export interface NumberingFormProps {
  fy: string;
  state: NumberingFormState;
  baseline: NumberingFormState;
  /** Field-level errors keyed by `${documentType}:prefix|padding|nextSeq`. */
  fieldErrors?: Record<string, string>;
  serverError?: string | null;
  /** Row change handler. */
  onChange: (next: NumberingFormState) => void;
  /** Open the "Continue from existing series" dialog for the given doc type. */
  onOpenSetNextSeq: (documentType: string) => void;
}

/**
 * Grid column template — kept in one place so header + rows align
 * exactly. Every track is a fixed or fr width (never `auto`): an `auto`
 * trailing column would size to its content independently in the header
 * (empty → 0px) vs each row (button/pill width), which throws the fr
 * columns out of alignment. The action column is a fixed 168px so the
 * "Set starting number" button and the "Locked" pill share one rail
 * and never wrap to a second line.
 */
const GRID_COLS =
  "sm:grid-cols-[32px_minmax(0,1.4fr)_120px_104px_minmax(150px,1fr)_168px]";

export function NumberingForm({
  fy,
  state,
  baseline,
  fieldErrors,
  serverError,
  onChange,
  onOpenSetNextSeq,
}: NumberingFormProps) {
  const patchRow = React.useCallback(
    (documentType: string, patch: Partial<NumberingCardValues>) => {
      const current = state[documentType];
      if (!current) return;
      onChange({ ...state, [documentType]: { ...current, ...patch } });
    },
    [state, onChange],
  );

  const documentTypes = Object.keys(state);

  return (
    <>
      {serverError ? (
        <Alert intent="bad" title="Couldn't save numbering" description={serverError} />
      ) : null}

      <FormCard
        id="numbering-table"
        icon={<Hash size={14} strokeWidth={2} aria-hidden />}
        title="Document sequences"
        description="One row per document type Flint issues numbers for. Format is fixed: PREFIX / FY / padded-sequence. GST-Rule-46-compliant — gap-free, no-reuse, auto-resets every April 1."
        badge={
          <StatusPill tone="sky" size="xs">
            FY {fy}
          </StatusPill>
        }
        bodyClassName="p-0"
      >
        {/* Header — hidden on narrow screens; row labels appear inline.
            Shares the card's px-5 gutter so column heads line up with the
            header/footer insets and the row content below. Styling matches
            the workspace table-header standard (data-table-card.tsx). */}
        <div
          className={cn(
            "hidden",
            "sm:grid sm:gap-3 sm:items-center sm:px-5 sm:py-3 sm:bg-bg/40 sm:border-b sm:border-line",
            GRID_COLS,
          )}
        >
          <span aria-hidden />
          <ColHead>Document</ColHead>
          <ColHead>Prefix</ColHead>
          <ColHead>Padding</ColHead>
          <ColHead>Next number</ColHead>
          <ColHead className="text-right">Starting number</ColHead>
        </div>

        <div className="flex flex-col divide-y divide-line">
          {documentTypes.map((dt) => {
            const row = state[dt]!;
            const base = baseline[dt]!;
            const dirty = isCardDirty(row, base);
            const preview = formatPreview(row.prefix, fy, row.nextSeq, row.padding);
            const prefixError = fieldErrors?.[`${dt}:prefix`];
            const paddingError = fieldErrors?.[`${dt}:padding`];
            const issued = row.nextSeq - 1;
            const Icon = DOC_TYPE_ICON[dt] ?? FileText;
            return (
              <div
                key={dt}
                className={cn(
                  "relative grid grid-cols-1 gap-3 px-5 py-3 items-start sm:items-center",
                  "transition-colors hover:bg-bg/60",
                  GRID_COLS,
                )}
                data-testid={`numbering-row-${dt}`}
              >
                {/* Dirty accent — 2px sky strip on the left edge of the row. */}
                {dirty ? (
                  <span
                    aria-hidden
                    className="absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full bg-sky-600"
                  />
                ) : null}

                {/* Icon chip */}
                <div
                  className={cn(
                    "inline-grid place-items-center h-8 w-8 rounded-md shrink-0",
                    issued > 0
                      ? "bg-sky-50 text-sky-700 ring-1 ring-sky-100"
                      : "bg-raised text-muted ring-1 ring-line",
                  )}
                  aria-hidden
                >
                  <Icon size={15} strokeWidth={2} />
                </div>

                {/* Document label + issued meta. `items-start` keeps the
                    issued pill hugging its label width instead of stretching
                    across the whole column. */}
                <div className="min-w-0 flex flex-col items-start gap-1">
                  <div className="max-w-full text-sm font-medium text-ink truncate">{row.label}</div>
                  {issued > 0 ? (
                    <StatusPill tone="ok" size="xs" dot className="max-w-full">
                      <span className="truncate">{`${issued} issued this FY`}</span>
                    </StatusPill>
                  ) : (
                    <p className="text-xs text-muted leading-tight">Not used yet</p>
                  )}
                </div>

                {/* Prefix */}
                <div className="flex flex-col gap-0.5 min-w-0">
                  <MobileLabel>Prefix</MobileLabel>
                  <Input
                    id={`numbering-${dt}-prefix`}
                    value={row.prefix}
                    maxLength={8}
                    autoCapitalize="characters"
                    spellCheck={false}
                    className="font-mono uppercase tracking-wide h-9"
                    aria-label={`${row.label} prefix`}
                    onChange={(e) =>
                      patchRow(dt, {
                        prefix: e.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9-]/g, "")
                          .slice(0, 8),
                      })
                    }
                  />
                  {prefixError ? (
                    <p className="text-xs text-bad leading-tight">{prefixError}</p>
                  ) : null}
                </div>

                {/* Padding */}
                <div className="flex flex-col gap-0.5 min-w-0">
                  <MobileLabel>Padding</MobileLabel>
                  <Select
                    value={String(row.padding)}
                    onValueChange={(v) =>
                      patchRow(dt, { padding: Number(v) as NumberingCardValues["padding"] })
                    }
                  >
                    <SelectTrigger
                      id={`numbering-${dt}-padding`}
                      className="h-9"
                      aria-label={`${row.label} padding`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[3, 4, 5, 6].map((p) => (
                        <SelectItem key={p} value={String(p)}>
                          {p} digits
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {paddingError ? (
                    <p className="text-xs text-bad leading-tight">{paddingError}</p>
                  ) : null}
                </div>

                {/* Preview — code-chip styled */}
                <div className="flex flex-col gap-0.5 min-w-0">
                  <MobileLabel>Next number</MobileLabel>
                  <div
                    className={cn(
                      "inline-flex items-center gap-1.5 px-2.5 h-9 rounded-sm",
                      "bg-sky-50/60 ring-1 ring-sky-100",
                      "font-mono text-sm text-sky-900 tabular-nums",
                    )}
                    title={preview}
                  >
                    <span className="truncate">{preview}</span>
                  </div>
                </div>

                {/* Action — set the starting number (migration helper),
                    or a Locked indicator once numbering has begun. */}
                <div className="flex sm:justify-end">
                  {row.canSetNextSeq ? (
                    <Button
                      type="button"
                      intent="ghost"
                      size="sm"
                      className="whitespace-nowrap ring-1 ring-line text-ink-2 hover:bg-raised"
                      onClick={() => onOpenSetNextSeq(dt)}
                      title="Coming from Tally or another system? Set the number to start from."
                    >
                      <Pencil size={12} strokeWidth={2.25} aria-hidden className="-ml-0.5 mr-1" />
                      Set starting number
                    </Button>
                  ) : (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 px-2 h-7",
                        "text-xs uppercase tracking-wider font-semibold text-muted",
                        "bg-raised ring-1 ring-line rounded-full",
                      )}
                      title="The starting number locks automatically once the first document is issued — GST Rule 46 forbids changing a live series."
                    >
                      <Lock size={9} strokeWidth={2.5} aria-hidden />
                      Locked
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer hint — replaces the removed top banner; quieter placement.
            Lives at the bottom of the flush body, so we re-apply the
            5-unit horizontal inset that the row padding uses. */}
        <p className="px-5 py-3 border-t border-line text-xs text-muted leading-relaxed">
          <span className="font-semibold text-ink-2">Migrating from Tally?</span> Set the prefix on
          a row, then click{" "}
          <span className="font-semibold text-ink-2">Set starting number</span> to continue from
          where your old sequence left off.
        </p>
      </FormCard>
    </>
  );
}

// ── Local primitives ────────────────────────────────────────────────

function ColHead({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("text-xs font-medium uppercase tracking-wide text-muted", className)}>
      {children}
    </div>
  );
}

/** Per-cell label that appears only when the row is stacked (narrow screens). */
function MobileLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs uppercase tracking-wide text-muted font-medium sm:hidden">
      {children}
    </div>
  );
}
